/**
 * Analytics Module
 *
 * Provides reusable Chart.js initialization functions for analytics pages.
 * Data is passed via data-* attributes on canvas elements.
 *
 * Usage:
 * <canvas id="myChart"
 *     data-chart-type="line"
 *     data-chart-data='{{ chart_data|json_encode }}'
 *     data-chart-label-field="date"
 *     data-chart-datasets='[{"label":"Revenue","field":"revenue","color":"#3b82f6","yAxisID":"y"},{"label":"Orders","field":"orders","color":"#10b981","yAxisID":"y1"}]'>
 * </canvas>
 */

import { $, $$, ready } from '../utils/dom.js';

// =============================================================================
// Chart Configuration Defaults
// =============================================================================

const DEFAULT_COLORS = {
    primary: 'rgb(59, 130, 246)',
    primaryBg: 'rgba(59, 130, 246, 0.1)',
    success: 'rgb(16, 185, 129)',
    successBg: 'rgba(16, 185, 129, 0.1)',
    warning: 'rgb(245, 158, 11)',
    warningBg: 'rgba(245, 158, 11, 0.1)',
    danger: 'rgb(239, 68, 68)',
    dangerBg: 'rgba(239, 68, 68, 0.1)',
    info: 'rgb(99, 102, 241)',
    infoBg: 'rgba(99, 102, 241, 0.1)'
};

const COLOR_PALETTE = [
    { border: DEFAULT_COLORS.primary, background: DEFAULT_COLORS.primaryBg },
    { border: DEFAULT_COLORS.success, background: DEFAULT_COLORS.successBg },
    { border: DEFAULT_COLORS.warning, background: DEFAULT_COLORS.warningBg },
    { border: DEFAULT_COLORS.danger, background: DEFAULT_COLORS.dangerBg },
    { border: DEFAULT_COLORS.info, background: DEFAULT_COLORS.infoBg }
];

// =============================================================================
// Chart Helpers
// =============================================================================

/**
 * Format a date for chart labels
 * @param {string} dateStr - Date string
 * @returns {string} Formatted date
 */
function formatDate(dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format hour for chart labels
 * @param {number} hour - Hour (0-23)
 * @returns {string} Formatted hour (e.g., "9 AM")
 */
function formatHour(hour) {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    return hour > 12 ? `${hour - 12} PM` : `${hour} AM`;
}

/**
 * Get default line chart options
 * @param {Object} overrides - Option overrides
 * @returns {Object} Chart options
 */
function getLineChartOptions(overrides = {}) {
    return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false
        },
        plugins: {
            legend: {
                display: true,
                position: 'top'
            }
        },
        scales: {
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: overrides.yAxisLabel || 'Value'
                }
            },
            ...(overrides.dualAxis ? {
                y1: {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    title: {
                        display: true,
                        text: overrides.y1AxisLabel || 'Count'
                    },
                    grid: {
                        drawOnChartArea: false
                    }
                }
            } : {})
        },
        ...overrides
    };
}

/**
 * Get default bar chart options
 * @param {Object} overrides - Option overrides
 * @returns {Object} Chart options
 */
function getBarChartOptions(overrides = {}) {
    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: overrides.showLegend !== false
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: !!overrides.yAxisLabel,
                    text: overrides.yAxisLabel || ''
                }
            }
        },
        ...overrides
    };
}

/**
 * Get default pie/doughnut chart options
 * @param {Object} overrides - Option overrides
 * @returns {Object} Chart options
 */
function getPieChartOptions(overrides = {}) {
    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'right'
            }
        },
        ...overrides
    };
}

// =============================================================================
// Chart Initialization Functions
// =============================================================================

/**
 * Create a line chart for revenue/orders (dual axis)
 * @param {string} canvasId - Canvas element ID
 * @param {Array} data - Chart data array
 * @param {Object} options - Additional options
 * @returns {Chart|null} Chart instance
 */
export function createRevenueOrdersChart(canvasId, data, options = {}) {
    const canvas = document.getElementById(canvasId);
    if (!canvas || typeof Chart === 'undefined') return null;

    const ctx = canvas.getContext('2d');
    const labelField = options.labelField || 'date';
    const labelFormatter = options.labelFormatter || formatDate;

    return new Chart(ctx, {
        type: 'line',
        data: {
            labels: data.map(item => labelFormatter(item[labelField])),
            datasets: [{
                label: 'Revenue',
                data: data.map(item => parseFloat(item.revenue || 0)),
                borderColor: DEFAULT_COLORS.primary,
                backgroundColor: DEFAULT_COLORS.primaryBg,
                tension: 0.1,
                yAxisID: 'y'
            }, {
                label: 'Orders',
                data: data.map(item => parseInt(item.orders || 0)),
                borderColor: DEFAULT_COLORS.success,
                backgroundColor: DEFAULT_COLORS.successBg,
                tension: 0.1,
                yAxisID: 'y1'
            }]
        },
        options: getLineChartOptions({
            dualAxis: true,
            yAxisLabel: 'Revenue ($)',
            y1AxisLabel: 'Number of Orders'
        })
    });
}

/**
 * Create a bar chart
 * @param {string} canvasId - Canvas element ID
 * @param {Array} labels - Chart labels
 * @param {Array} values - Chart values
 * @param {Object} options - Additional options
 * @returns {Chart|null} Chart instance
 */
export function createBarChart(canvasId, labels, values, options = {}) {
    const canvas = document.getElementById(canvasId);
    if (!canvas || typeof Chart === 'undefined') return null;

    const ctx = canvas.getContext('2d');

    return new Chart(ctx, {
        type: 'bar',
        data: {
            labels: labels,
            datasets: [{
                label: options.label || 'Value',
                data: values,
                backgroundColor: options.backgroundColor || DEFAULT_COLORS.primaryBg,
                borderColor: options.borderColor || DEFAULT_COLORS.primary,
                borderWidth: 1
            }]
        },
        options: getBarChartOptions({
            yAxisLabel: options.yAxisLabel
        })
    });
}

/**
 * Create an hourly distribution chart
 * @param {string} canvasId - Canvas element ID
 * @param {Array} data - Hourly data with 'hour' property
 * @param {string} valueField - Field to use for values
 * @param {Object} options - Additional options
 * @returns {Chart|null} Chart instance
 */
export function createHourlyChart(canvasId, data, valueField = 'orders', options = {}) {
    const canvas = document.getElementById(canvasId);
    if (!canvas || typeof Chart === 'undefined') return null;

    const ctx = canvas.getContext('2d');

    // Create array for all 24 hours
    const hourlyData = Array.from({ length: 24 }, (_, i) => {
        const hourData = data.find(item => parseInt(item.hour) === i);
        return hourData ? parseFloat(hourData[valueField] || 0) : 0;
    });

    return new Chart(ctx, {
        type: 'bar',
        data: {
            labels: Array.from({ length: 24 }, (_, i) => formatHour(i)),
            datasets: [{
                label: options.label || valueField,
                data: hourlyData,
                backgroundColor: options.backgroundColor || DEFAULT_COLORS.primaryBg,
                borderColor: options.borderColor || DEFAULT_COLORS.primary,
                borderWidth: 1
            }]
        },
        options: getBarChartOptions({
            yAxisLabel: options.yAxisLabel
        })
    });
}

/**
 * Create a pie or doughnut chart
 * @param {string} canvasId - Canvas element ID
 * @param {Array} labels - Segment labels
 * @param {Array} values - Segment values
 * @param {Object} options - Additional options (type: 'pie' or 'doughnut')
 * @returns {Chart|null} Chart instance
 */
export function createPieChart(canvasId, labels, values, options = {}) {
    const canvas = document.getElementById(canvasId);
    if (!canvas || typeof Chart === 'undefined') return null;

    const ctx = canvas.getContext('2d');
    const colors = COLOR_PALETTE.map(c => c.border);
    const bgColors = COLOR_PALETTE.map(c => c.background);

    return new Chart(ctx, {
        type: options.type || 'doughnut',
        data: {
            labels: labels,
            datasets: [{
                data: values,
                backgroundColor: bgColors.slice(0, values.length),
                borderColor: colors.slice(0, values.length),
                borderWidth: 1
            }]
        },
        options: getPieChartOptions()
    });
}

// =============================================================================
// Auto-Initialization from Data Attributes
// =============================================================================

/**
 * Initialize charts from data attributes
 */
function initChartsFromAttributes() {
    const chartContainers = $$('[data-chart-type]');

    chartContainers.forEach(canvas => {
        const chartType = canvas.dataset.chartType;
        const rawData = canvas.dataset.chartData;

        if (!rawData || !chartType) return;

        try {
            const data = JSON.parse(rawData);
            const labelField = canvas.dataset.chartLabelField || 'date';
            const valueField = canvas.dataset.chartValueField || 'value';

            switch (chartType) {
                case 'revenue-orders':
                    createRevenueOrdersChart(canvas.id, data, { labelField });
                    break;
                case 'hourly':
                    createHourlyChart(canvas.id, data, valueField, {
                        label: canvas.dataset.chartLabel,
                        yAxisLabel: canvas.dataset.chartYaxisLabel
                    });
                    break;
                case 'bar':
                    const barLabels = data.map(d => d[labelField]);
                    const barValues = data.map(d => parseFloat(d[valueField] || 0));
                    createBarChart(canvas.id, barLabels, barValues, {
                        label: canvas.dataset.chartLabel,
                        yAxisLabel: canvas.dataset.chartYaxisLabel
                    });
                    break;
                case 'pie':
                case 'doughnut':
                    const pieLabels = data.map(d => d[labelField]);
                    const pieValues = data.map(d => parseFloat(d[valueField] || 0));
                    createPieChart(canvas.id, pieLabels, pieValues, { type: chartType });
                    break;
            }
        } catch (e) {
            console.error('Error initializing chart:', canvas.id, e);
        }
    });
}

// =============================================================================
// Date Filter Functions
// =============================================================================

/**
 * Apply date range filter
 * @param {HTMLFormElement} form - Filter form
 */
export function applyDateFilter(form) {
    if (form) {
        form.submit();
    }
}

/**
 * Set date range preset
 * @param {string} preset - Preset name (today, yesterday, last7, last30, thisMonth, lastMonth)
 */
export function setDatePreset(preset) {
    const today = new Date();
    let startDate, endDate;

    switch (preset) {
        case 'today':
            startDate = endDate = formatDateInput(today);
            break;
        case 'yesterday':
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            startDate = endDate = formatDateInput(yesterday);
            break;
        case 'last7':
            endDate = formatDateInput(today);
            const week = new Date(today);
            week.setDate(week.getDate() - 6);
            startDate = formatDateInput(week);
            break;
        case 'last30':
            endDate = formatDateInput(today);
            const month = new Date(today);
            month.setDate(month.getDate() - 29);
            startDate = formatDateInput(month);
            break;
        case 'thisMonth':
            startDate = formatDateInput(new Date(today.getFullYear(), today.getMonth(), 1));
            endDate = formatDateInput(today);
            break;
        case 'lastMonth':
            const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
            startDate = formatDateInput(lastMonth);
            endDate = formatDateInput(lastMonthEnd);
            break;
        default:
            return;
    }

    const startInput = document.getElementById('start_date') || document.getElementById('date_start');
    const endInput = document.getElementById('end_date') || document.getElementById('date_end');

    if (startInput) startInput.value = startDate;
    if (endInput) endInput.value = endDate;
}

/**
 * Format date for input value (YYYY-MM-DD)
 * @param {Date} date
 * @returns {string}
 */
function formatDateInput(date) {
    return date.toISOString().split('T')[0];
}

// =============================================================================
// Auto-Initialization
// =============================================================================

function initAnalytics() {
    // Wait for Chart.js to be available
    if (typeof Chart !== 'undefined') {
        initChartsFromAttributes();
    } else {
        // Retry after a short delay if Chart.js is still loading
        setTimeout(() => {
            if (typeof Chart !== 'undefined') {
                initChartsFromAttributes();
            }
        }, 100);
    }
}

ready(initAnalytics);

// Expose globally for legacy usage
window.createRevenueOrdersChart = createRevenueOrdersChart;
window.createBarChart = createBarChart;
window.createHourlyChart = createHourlyChart;
window.createPieChart = createPieChart;
window.applyDateFilter = applyDateFilter;
window.setDatePreset = setDatePreset;

export default {
    createRevenueOrdersChart,
    createBarChart,
    createHourlyChart,
    createPieChart,
    applyDateFilter,
    setDatePreset,
    DEFAULT_COLORS,
    COLOR_PALETTE
};
